/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { compose } from 'lodash/fp';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';

import {
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';

import {
    QuestionFormWithOptionsBase,
    QuestionFormWithOptionsBaseProps,
    QuestionFormWithOptionsBaseStates
} from '../QuestionFormWithOptionsBase';
import {
    QTYPES,
    OPTION_TYPES,
    OptionData,
    MULTIMEDIA_TYPES,
    OptionButton
} from '@nexxt/common/types';
import NotifyHOC from '../../../../../../HOC/Notifications';

/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import QuestionSettings from '../../../common/questionSettings';
import {
    FOLLOW_UP_TEXTS,
    DEFAULT_TEXT
} from '../../../../../../constants/defaultText';
import CheckboxMultiSelect from '../../../../../common/reactSelect/CheckboxMultiSelect';
import ImageModal from '../../../common/ImageModal';
import Toggle from '../../../../../common/Toggle';
import { Notes } from '../../../../../common/notes';
import {
    parseAlertMessage,
    alertMessage
} from '../../../../../../constants/alertMessage';
import { questionErrorMessage } from '../../../../../../constants/applicationMessage';
import ComputerVision from '../../../common/ComputerVision';
import CollaspeButton from '../../../common/optionCard/CollaspeButton';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import {
    DEFAULT_OPTION,
    DEFAULT_TRANSLATION_BUTTONS
} from '../../../../../../constants/defaultOption';
import { ALERT_MESSAGE_TYPES } from '../../../../../../interface/project.interface';
import MultimediaBubble from '../../../common/MultimediaBubble';

interface HotspotProps extends QuestionFormWithOptionsBaseProps {
    // nothing special... yet!
    type: QTYPES.HOTSPOT | QTYPES.TREEMAN;
    t?: (text: string, options?: Record<string, boolean>) => string; // translate function
}

interface HotspotState extends QuestionFormWithOptionsBaseStates {
    // nothing special... yet!
    followUpType: any[];
    isProcessing: boolean;
    enableComputerVision: boolean;
    isBoxListOpen: boolean;
}
class Hotspot extends QuestionFormWithOptionsBase<HotspotProps, HotspotState> {
    constructor(p: HotspotProps) {
        super(p, p.type);
        const { isNewQuestion } = this.props;
        const isHotspot = p.type === QTYPES.HOTSPOT;
        const buttons = DEFAULT_TRANSLATION_BUTTONS(
            p.type,
            this.props.t,
            this.props.currentProject.languages.map((l) => l.value)
        ) as OptionButton[];
        const question = {
            ...this.state.question,
            showFollowUp: this.props.isNewQuestion
                ? true
                : this.state.question.showFollowUp,
            showContinueButton: true,
            showNotApplicableOption: this.props.isNewQuestion
                ? false
                : this.state.question.showNotApplicableOption,
            buttons: isNewQuestion ? buttons : this.state.question.buttons,
            mediaURL:
                !isHotspot && isNewQuestion
                    ? `${STATIC_STORAGE_URL}/img/Projective_Treeman.png`
                    : this.state.question.mediaURL,
            isVisible: isNewQuestion
                ? isHotspot
                    ? true
                    : false
                : this.state.question.isVisible,
            options: isNewQuestion
                ? isHotspot
                    ? []
                    : DEFAULT_OPTION.OPTIONS.TREEMAN
                : this.state.question.options,
            min: this.props.isNewQuestion ? 1 : this.state.question.min,
            max: this.props.isNewQuestion ? 1 : this.state.question.max,
            selectedOptionType: OPTION_TYPES.IMAGE_ONLY
        };

        this.state = {
            ...this.state,
            showCardSettings: true,
            followUpType: FOLLOW_UP_TEXTS.HOTSPOT,
            isProcessing: false,
            enableComputerVision: true,
            isBoxListOpen: false,
            question
        };
    }

    // Add a new area as an option
    addNewOption = () => {
        const newObjectBox: OptionData = {
            new: true,
            label: '',
            value: String(this.state.question.options.length + 1),
            coordinates: {
                rotation: 0,
                x: 100,
                y: 100,
                height: 200,
                width: 200
            }
        };
        const options = [...this.state.question.options, newObjectBox].map(
            (opt, i) => ({ ...opt, value: String(i + 1) })
        );
        this.setState(
            {
                ...this.state,
                question: {
                    ...this.state.question,
                    options
                }
            },
            this.setEditFlag
        );
    };

    // Remove all options(areas) and the uploaded image
    clearComputerVision = () => {
        this.setState(
            {
                ...this.state,
                question: {
                    ...this.state.question,
                    mediaURL: null
                }
            },
            () => {
                this.clearOptions();
                this.setEditFlag();
            }
        );
    };

    toggleBoxListExpand = () => {
        this.setState({ isBoxListOpen: !this.state.isBoxListOpen });
    };

    handleBoxTextUpdate = (value, newLabel) => {
        const newBoxes = this.state.question.options.map((opt) => {
            if (opt.value === value) {
                return { ...opt, label: newLabel };
            } else {
                return opt;
            }
        });
        this.setState(
            {
                question: { ...this.state.question, options: newBoxes }
            },
            this.setEditFlag
        );
    };

    render() {
        const { currentProject } = this.props;
        const {
            question,
            isSettingBoxLayoutOpen,
            isOptionExpand,
            isImageModalOpen,
            followUpType,
            isOptionSettingsOpen
        } = this.state;
        const enableProbingToggle = question.type === QTYPES.TREEMAN;
        const questionError =
            (this.state.showError && this.state.questionError) || {};
        return (
            <div>
                <div className="project-add--question-builder">
                    {isImageModalOpen && (
                        <ImageModal
                            isOpen={isImageModalOpen}
                            onClose={this.toggleImageModal}
                            imageURL={this.state.currentImage}
                            question={question}
                            cvEnabled={true}
                            addNewOption={this.addNewOption}
                            clearOptions={this.clearOptions}
                            handleOptionFields={this.handleOptionFields}
                        />
                    )}
                    <BuilderHeader
                        toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                        isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                        showAlert={!this.props.editEnabled}
                        alertMessages={this.getWarningMessage()}
                        question={question}
                        setValue={this.setQuestion}
                        errorObject={this.state.questionError}
                    />
                    {/** Begin of builder-body */}
                    <div className="builder-body">
                        {isSettingBoxLayoutOpen && (
                            <QuestionSettings
                                onClose={this.toggleSettingBoxLayout}
                                question={question}
                                questionSettings={{
                                    allowUserEdit: true,
                                    boxVisibility: true
                                }}
                                setValue={this.setQuestion}
                                disabled={!this.props.editEnabled}
                            />
                        )}
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            addNewBubble={this.NEW_addNewEditor}
                            removeBubble={this.NEW_removeEditor}
                            disabled={!this.props.editEnabled}
                            questionError={questionError}
                            settings={{ showClose: true }}
                        />
                        {question.options.length ? (
                            <Notes
                                note={parseAlertMessage(
                                    DEFAULT_TEXT.QUESTION.HOTSPOT.NOTES,
                                    question.options.length
                                )}
                            />
                        ) : !question.mediaURL ? (
                            <Notes
                                type={ALERT_MESSAGE_TYPES.WARN}
                                note={DEFAULT_TEXT.QUESTION.HOTSPOT.EMPTY_NOTES}
                            />
                        ) : (
                            <Notes
                                type={ALERT_MESSAGE_TYPES.WARN}
                                note={
                                    this.state.isProcessing
                                        ? DEFAULT_TEXT.QUESTION.HOTSPOT
                                              .CV_PROCESSING
                                        : DEFAULT_TEXT.QUESTION.HOTSPOT
                                              .WARN_NOTES
                                }
                            />
                        )}
                        <ComputerVision
                            imageURL={this.state.question.mediaURL}
                            question={question}
                            handleOptionFields={this.handleOptionFields}
                            addNewOption={this.addNewOption}
                            clearOptions={this.clearOptions}
                            setValue={this.setQuestion}
                            clearComputerVision={this.clearComputerVision}
                            enableComputerVision={
                                this.state.enableComputerVision
                            }
                            toggleEnableComputerVision={() => {
                                this.setState({
                                    enableComputerVision:
                                        !this.state.enableComputerVision
                                });
                            }}
                            updateOptions={(optionedCV) => {
                                this.setState(
                                    {
                                        ...this.state,
                                        question: {
                                            ...this.state.question,
                                            options: optionedCV
                                        }
                                    },
                                    this.setEditFlag
                                );
                            }}
                            isLoading={this.state.isProcessing}
                            updateIsLoading={(value) => {
                                this.setState({ isProcessing: value });
                            }}
                            toggleMediaLibrary={this.toggleMediaLibrary(
                                'image'
                            )}
                            toastNotification={this.props.toastNotification}
                            selectMedia={this.selectMedia}
                        />
                        {!this.state.isProcessing &&
                            question.mediaURL &&
                            questionError &&
                            question.options.length < 1 && (
                                <div
                                    className={`form-group ${
                                        questionError ? 'error' : ''
                                    }`}
                                >
                                    <label className="form-group__label form-group__label--block">
                                        {
                                            questionErrorMessage.EMPTY_AREA_OPTIONS
                                        }
                                    </label>
                                </div>
                            )}
                        <div className="option-settings-block">
                            {question.type === QTYPES.HOTSPOT && (
                                <div className="form-group-header display-flex">
                                    Hotspot List
                                    <CollaspeButton
                                        isOptionExpand={
                                            this.state.isBoxListOpen
                                        }
                                        toggleOptionExpand={
                                            this.toggleBoxListExpand
                                        }
                                    />
                                </div>
                            )}
                            {this.state.isBoxListOpen && (
                                <div className="boxlist-item--container">
                                    {question.options?.length > 0 ? (
                                        <>
                                            <div className="boxlist-item">
                                                <div className="boxlist-item--content">
                                                    <span className="boxlist-item--num boxlist-item--header">
                                                        Area #
                                                    </span>
                                                    <span className="boxlist-item--header">
                                                        Area Label
                                                    </span>
                                                </div>
                                            </div>
                                            {question.options
                                                ?.sort(
                                                    (a, b) =>
                                                        Number(a.value) -
                                                        Number(b.value)
                                                )
                                                .map((opt, i) => (
                                                    <div
                                                        key={i}
                                                        className="boxlist-item"
                                                    >
                                                        <div className="boxlist-item--content">
                                                            <span className="boxlist-item--num">
                                                                {opt.value}
                                                            </span>
                                                            <input
                                                                value={
                                                                    opt.label
                                                                }
                                                                className="form-group__control"
                                                                disabled={
                                                                    !this.props
                                                                        .editEnabled
                                                                }
                                                                onChange={(e) =>
                                                                    this.handleBoxTextUpdate(
                                                                        opt.value,
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </>
                                    ) : (
                                        <div className="boxlist-item">
                                            <div className="boxlist-item--content">
                                                <span>
                                                    There are currently no
                                                    hotspot areas on this image.
                                                    Please add a new area on the
                                                    image above.
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {question.type === QTYPES.HOTSPOT && (
                            <div className="option-settings-block">
                                {/* <div className="form-group-header display-flex">
                                    Options
                                    <Settings
                                    label="Option settings"
                                    onClick={this.toggleOptionSettings}
                                />
                                </div> */}
                                {/* {isOptionSettingsOpen &&
                                ((
                                    <OptionSetting
                                        setValue={this.setQuestion}
                                        question={question}
                                        settings={{
                                            showOptionNotApplicable: true
                                        }}
                                        toggleOptionSettings={
                                            this.toggleOptionSettings
                                        }
                                        onSaveOptionSettings={
                                            this.onSaveOptionSettings
                                        }
                                        optionSettingErrors={
                                            this.state.optionSettingErrors || {}
                                        }
                                        disabled={!this.props.editEnabled}
                                    />
                                ) ||
                                    null)} */}
                                <div className="row">
                                    <label className="col-md-12 form-group-header">
                                        Option types
                                    </label>
                                    <div className="col-md-12 form-group">
                                        <CheckboxMultiSelect
                                            options={DEFAULT_TRANSLATION_BUTTONS(
                                                this.state.question.type,
                                                this.props.t,
                                                this.props.currentProject.languages.map(
                                                    (l) => l.value
                                                )
                                            )}
                                            value={question.buttons || []}
                                            onChangeCallback={(response) =>
                                                this.setQuestion(
                                                    'buttons',
                                                    response
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="form-group-header display-flex">
                                    Options
                                    <CollaspeButton
                                        isOptionExpand={
                                            this.state.isOptionExpand
                                        }
                                        toggleOptionExpand={
                                            this.toggleOptionExpand
                                        }
                                    />
                                </div>

                                {(questionError &&
                                    (questionError.buttons || {}).isError && (
                                        <div
                                            className={`form-group ${
                                                questionError ? 'error' : ''
                                            }`}
                                        >
                                            <label className="form-group__label form-group__label--block">
                                                {questionError.buttons
                                                    ?.emptyButtons ||
                                                    alertMessage.BUTTON_ERROR}
                                            </label>
                                        </div>
                                    )) ||
                                    null}
                                {/** Show Option Cards according to option type */}
                                {isOptionExpand &&
                                    this.renderOptionCard(
                                        OPTION_TYPES.IMAGE_AND_TEXT,
                                        {
                                            disableLabel: false,
                                            disableValue: true,
                                            disableRemove: true,
                                            disableDrag: true,
                                            disableImage: true
                                        },
                                        'buttons'
                                    )}
                            </div>
                        )}

                        <div className="form-group-header">
                            Follow-up question
                            <Toggle
                                onChange={(value) =>
                                    this.setQuestion('showFollowUp', value)
                                }
                                checked={question.showFollowUp || false}
                                disabled={!this.props.editEnabled}
                                className="ml-auto"
                            />
                        </div>
                        {question.type !== QTYPES.HOTSPOT &&
                            followUpType
                                .filter((followUp) =>
                                    question.buttons.find(
                                        (button) =>
                                            button.value === followUp.value
                                    )
                                )
                                .map((followUpText, i) => {
                                    const followUpLabel =
                                        question.type === QTYPES.HOTSPOT
                                            ? followUpText.label
                                            : 'Reason for the selection';
                                    return (
                                        <MultimediaBubble
                                            key={`followUp-${followUpText.keyName}`}
                                            languages={currentProject.languages}
                                            keyName={followUpText.keyName}
                                            question={question}
                                            setValue={this.setQuestion}
                                            setValueWithLanguage={
                                                this
                                                    .NEW_setQuestionTextTranslation
                                            }
                                            questionText={followUpLabel}
                                            questionError={questionError}
                                            defaultText={followUpText.text}
                                            disabled={!this.props.editEnabled}
                                        />
                                    );
                                })}

                        {question.type === QTYPES.HOTSPOT && (
                            <>
                                <div className="form-group-header">
                                    Number of areas to select (min/max)
                                </div>
                                <div className="row">
                                    <div
                                        className={classnames(
                                            'col-md-6 form-group',
                                            {
                                                error:
                                                    questionError &&
                                                    questionError.optionCard
                                            }
                                        )}
                                    >
                                        <input
                                            type="number" //Keep validation here
                                            className="form-group__control opt-min"
                                            placeholder="Minimum number of area selects"
                                            onChange={(event) =>
                                                this.setQuestion(
                                                    'min',
                                                    parseInt(event.target.value)
                                                )
                                            }
                                            value={question.min}
                                            name="min"
                                            min="1"
                                        />
                                    </div>
                                    <div
                                        className={classnames(
                                            'col-md-6 form-group',
                                            {
                                                error:
                                                    questionError &&
                                                    questionError.optionCard
                                            }
                                        )}
                                    >
                                        <input
                                            type="number" //Keep validation here
                                            className="form-group__control opt-max"
                                            placeholder="Maximum number of area selects"
                                            onChange={(event) =>
                                                this.setQuestion(
                                                    'max',
                                                    parseInt(event.target.value)
                                                )
                                            }
                                            value={question.max}
                                            name="max"
                                            min="1"
                                        />
                                    </div>
                                </div>
                                {(questionError &&
                                    questionError?.optionCard && (
                                        <div
                                            className={`form-group ${
                                                questionError ? 'error' : ''
                                            }`}
                                        >
                                            <label className="form-group__label form-group__label--block">
                                                {questionError?.optionCard}
                                            </label>
                                        </div>
                                    )) ||
                                    null}
                            </>
                        )}
                        <MultimediaBubble
                            languages={currentProject.languages}
                            question={question}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            questionError={questionError}
                            keyName="continueTexts"
                            disabled={!this.props.editEnabled}
                            disableAddBubble
                            defaultTextType={MULTIMEDIA_TYPES.TEXT}
                            questionText="Continue button text"
                            defaultText="BUTTON.CONTINUE"
                            settings={{ noBorder: true, useTranslation: true }}
                        />
                        {(question.showNotApplicableOption && (
                            <MultimediaBubble
                                languages={currentProject.languages}
                                question={question}
                                setValue={this.setQuestion}
                                setValueWithLanguage={
                                    this.NEW_setQuestionTextTranslation
                                }
                                questionError={questionError}
                                keyName="notApplicableTexts"
                                disabled={!this.props.editEnabled}
                                disableAddBubble
                                defaultTextType={MULTIMEDIA_TYPES.TEXT}
                                questionText="Not Applicable text"
                                defaultText="BUTTON.NOT_APPLICABLE"
                                settings={{
                                    noBorder: true,
                                    useTranslation: true
                                }}
                            />
                        )) ||
                            null}
                    </div>
                    {/** End of builder-body */}
                </div>
            </div>
        );
    }
}

export default NotifyHOC(
    compose(
        withTranslation(),
        connect(
            mapStateToQuestionFormBaseProps,
            mapDispatchToQuestionFormBaseProps
        ),
        withRouter
    )(Hotspot)
);
